<script setup lang="ts">
import { EmptyState, TraceIdAlert } from '@yc/ui-kit'
import { ref } from 'vue'

import TracePanel from '../components/TracePanel.vue'
import { traceByDocNo, type TraceBundle } from '../api/shipments'

const keyword = ref('')
const loading = ref(false)
/** 追溯失败：单号不存在（404）、403、网络错误都走这里 */
const error = ref<unknown>(null)
const trace = ref<TraceBundle | null>(null)
/** 已经查过一次：用来区分「还没查」与「查了但没找到」两种空态 */
const searched = ref(false)

async function lookup(): Promise<void> {
  const docNo = keyword.value.trim()
  if (!docNo) return
  loading.value = true
  error.value = null
  trace.value = null
  searched.value = true
  try {
    trace.value = await traceByDocNo(docNo)
  } catch (e) {
    error.value = e
  } finally {
    loading.value = false
  }
}

function reset(): void {
  keyword.value = ''
  trace.value = null
  error.value = null
  searched.value = false
}
</script>

<template>
  <div class="yc-trace-page">
    <header class="yc-trace-page__bar">
      <el-input
        v-model="keyword"
        class="yc-trace-page__keyword"
        placeholder="输入运单号，如 YD202610050001"
        clearable
        @keyup.enter="lookup"
        @clear="reset"
      />
      <el-button type="primary" :loading="loading" @click="lookup">追溯</el-button>
      <el-button v-if="trace || searched" @click="reset">清空</el-button>
      <span class="yc-trace-page__hint">
        按运单号查全程：轨迹事件、签收记录、回单与异常一次给全。
      </span>
    </header>

    <div v-if="error" class="yc-trace-page__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-trace-page__card">
      <template v-if="trace">
        <TracePanel :trace="trace" />
      </template>

      <EmptyState
        v-else-if="searched && !error && !loading"
        :title="`没有找到运单 ${keyword.trim()}`"
        description="请确认运单号是否完整（形如 YD202610050001，可在运单管理列表里复制），或到运单管理里核对。"
        tone="warn"
      >
        <el-button @click="reset">重新输入</el-button>
      </EmptyState>

      <EmptyState
        v-else-if="!loading"
        title="全程追溯"
        description="输入运单号查询这票货从建单、装车、发运、到达到签收的完整过程。"
      />
    </el-card>
  </div>
</template>

<style scoped lang="scss">
.yc-trace-page {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 300px;
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }
}
</style>
